import React, { useEffect, useState } from "react";
import SettingsTitle from "./settings-ui-components/SettingsTitle";
import SettingsCard from "./settings-ui-components/SettingsCard";
import logger from "../../../../lib/logger";
import { MondayApiQueryInstance } from "../../../../services/monday/monday-service-ts";
import { planDetails } from "../../../../data/constants/simbPlans";
import ButtonComponent from "../../../Button";
import { Skeleton } from "@vibe/core";
import { SettingSwitch } from "../SettingSwitch";
import VisualIndicatorSetting from "./visual-indicator/VisualIndicatorSetting";
import { UsersGrid } from "@components/AuthorizedUsers";
import { useAccountSettings } from "../../../../hooks/api";
import mondaySdk from "monday-sdk-js";
import { useLocation } from "react-router-dom";
import { takePendingSetting } from "../../../../utils/requests/reauthorization";

const monday = mondaySdk();

const AccountSettings = () => {
  const [subscriptionData, setSubscriptionData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [switchValues, setSwitchValues] = useState<any>({});
  const location = useLocation();

  const { accountSettingsData, updateAccountSetting, mutate } = useAccountSettings();

  useEffect(() => {
    if (accountSettingsData) {
      setSwitchValues(accountSettingsData);
    }
  }, [accountSettingsData]);

  // Landing here with ?authorized=… means the user was sent to monday to authorize after
  // a token_invalid failure (expired/revoked token). Missing scopes now go through the
  // admin approval flow instead, so this callback only handles real auth failures.
  // Finish what they started: re-apply the remembered setting, or tell them it stays off.
  useEffect(() => {
    const authorized = new URLSearchParams(location.search).get("authorized");
    if (authorized === null) return;
    const pendingSetting = takePendingSetting();
    if (!pendingSetting) return;

    if (authorized === "true") {
      setSwitchValues((prev: any) => ({ ...prev, [pendingSetting]: true }));
      updateAccountSetting(pendingSetting, true, { afterReauthorization: true })
        .then(() => mutate?.())
        .catch(() => setSwitchValues((prev: any) => ({ ...prev, [pendingSetting]: false })));
    } else {
      monday.execute("notice", {
        message: "Authorization was not completed, so the setting stays off.",
        type: "error",
        timeout: 8000,
      });
    }
    // Once, on mount: the query string does not change while the modal is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      setLoading(true);
      const response: any = await MondayApiQueryInstance.getSubscriptionData();

      setSubscriptionData(response.app_subscription[0]);
      try {
      } catch (error: any) {
        logger.error("Error fetching subscription data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionData();
  }, []);

  const openPlanSelection = () => {
    monday.execute("openPlanSelection", {
      isInPlanSelection: false,
    });
  };

  return (
    <div className="space-y-4">
      <SettingsTitle title="Account Settings" />

      {/* Subscription Details */}
      <SettingsCard
        title="Subscription"
        subtitle="Manage your subscription and billing preferences"
      >
        <div className="space-y-2 text-sm">
          {loading ? (
            <>
              <Skeleton type="text" size="small" />
              <Skeleton type="text" size="small" />
              <Skeleton type="text" size="small" />
            </>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  <p>Plan:</p>
                  <p className="font-medium">{planDetails[subscriptionData?.plan_id]?.name}</p>
                </div>
                <div>
                  <ButtonComponent
                    text="Change Plan"
                    size="small"
                    onClick={() => openPlanSelection()}
                  />
                </div>
              </div>
              {subscriptionData?.plan_id !== "simb_free_plan" && (
                <>
                  <div className="flex gap-2">
                    <p>Billing Cycle:</p>
                    <p className="font-medium">{subscriptionData?.billing_period}</p>
                  </div>
                  <div className="flex gap-2">
                    <p>Amount:</p>
                    <p className="font-medium">
                      {subscriptionData?.billing_period === "monthly"
                        ? planDetails[subscriptionData?.plan_id]?.monthlyPrice
                        : planDetails[subscriptionData?.plan_id]?.yearlyPrice}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <p>Renewal Date:</p>
                    <p className="font-medium">{subscriptionData?.renewal_date}</p>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </SettingsCard>

      {/* Preferences */}
      <SettingsCard title="Preferences">
        <div className="space-y-2">
          <VisualIndicatorSetting
            settings={accountSettingsData}
            enabled={switchValues?.visual_indicator_setting}
            setSwitchValues={setSwitchValues}
            updateAccountSetting={updateAccountSetting}
            onSettingsChanged={() => mutate()}
          />
          <SettingSwitch
            initialValue={switchValues?.sync_deletion_setting}
            label="Sync item deletion across boards"
            settingKey="sync_deletion_setting"
            tooltip="When an item is deleted on monday all items connected to it will be deleted"
            setNewValue={setSwitchValues}
            updateFunction={(value) => updateAccountSetting("sync_deletion_setting", value)}
          />
          {/* <SettingSwitch
            initialValue={switchValues?.shared_updates}
            label="Shared Updates"
            settingKey="shared_updates"
            tooltip="Allows updates to sync between shared items. Board settings and item settings will overide this."
            setNewValue={setSwitchValues}
            updateFunction={(value) => updateAccountSetting("shared_updates", value)}
          />
          <SettingSwitch
            initialValue={switchValues?.shared_subitems}
            label="Shared Subitems"
            settingKey="shared_subitems"
            tooltip="Allows subitems to sync between shared items. Board settings and item settings will overide this."
            setNewValue={setSwitchValues}
            updateFunction={(value) => updateAccountSetting("shared_subitems", value)}
          /> */}
          <SettingSwitch
            initialValue={switchValues?.item_location_setting}
            label="Item Locations Column"
            settingKey="item_location_setting"
            tooltip="A column that displays the locations of each shared item will be added to shared boards."
            setNewValue={setSwitchValues}
            updateFunction={(value) => updateAccountSetting("item_location_setting", value)}
          />
          <SettingSwitch
            initialValue={switchValues?.update_source_attribution_setting}
            label="Show Source Board on Synced Updates"
            settingKey="update_source_attribution_setting"
            tooltip="Synced copies of updates get a 'Posted From' footer linking back to the original update on its source board."
            setNewValue={setSwitchValues}
            updateFunction={(value) =>
              updateAccountSetting("update_source_attribution_setting", value)
            }
          />
          <SettingSwitch
            label="Sync item descriptions"
            settingKey="item_description_sync_setting"
            tooltip="Keeps the description of shared items identical on every board. Checked every 6 hours; the most recently edited copy wins. Text and structure are synced. Colors, highlights and attachments are not. Needs the monday docs:write permission. An admin on your monday account must approve it in Administration > Apps > Same Item Multiple Boards > Update."
            // Opt-in: accounts with no stored value are treated as OFF by the cron.
            initialValue={switchValues?.item_description_sync_setting ?? false}
            setNewValue={setSwitchValues}
            updateFunction={(value) => updateAccountSetting("item_description_sync_setting", value)}
          />
        </div>
      </SettingsCard>

      {/* Authorization */}

      <SettingsCard
        title="Authorization"
        subtitle="For updates to sync users need to be authorized"
      >
        <div className="space-y-4">
          <SettingSwitch
            label="Notify new users to authorize"
            settingKey="new_user_authorization_notice_setting"
            tooltip="New members of your monday account get a one-time notification asking them to authorize the app, so the updates they post on shared items stay in sync. Turn off to onboard new users at your own pace."
            // Accounts created before this setting existed have no stored value; the cron treats that as ON.
            initialValue={switchValues?.new_user_authorization_notice_setting ?? true}
            setNewValue={setSwitchValues}
            updateFunction={(value) =>
              updateAccountSetting("new_user_authorization_notice_setting", value)
            }
          />
          <UsersGrid />
        </div>
      </SettingsCard>
    </div>
  );
};

export default AccountSettings;
